/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  type CreateRoomPayload,
  type Room,
  type UpdateRoomPayload,
  roomsApi,
} from '../api/rooms.js';

const KEY = ['rooms'] as const;

function useInvalidate(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: KEY });
  };
}

export function useRooms(activeOnly = false): UseQueryResult<readonly Room[]> {
  return useQuery({ queryKey: [...KEY, activeOnly], queryFn: () => roomsApi.list(activeOnly) });
}

export function useCreateRoom(): UseMutationResult<Room, Error, CreateRoomPayload> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (p: CreateRoomPayload) => roomsApi.create(p),
    onSuccess: invalidate,
  });
}

export function useUpdateRoom(): UseMutationResult<
  Room,
  Error,
  { id: string; payload: UpdateRoomPayload }
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateRoomPayload }) =>
      roomsApi.update(id, payload),
    onSuccess: invalidate,
  });
}
